import {
  Card,
  Content,
  Divider,
  Heading,
  Inline,
  InlineItem,
  Placeholder,
  Secondary,
  Stack,
  Strong,
  Text,
} from '@kinesis/bungle';
import type { Place } from '@/types';
import { groupPlaceTree } from '@/data/places';
import { renderPlaceType } from '@/data/labels';

// Pluralise a humanised place-type label. The y-rule matters: the village
// vocabulary includes `territorial-authority` ("territorial authorities",
// never "authoritys") and `country`.
const pluralise = (label: string) =>
  /[^aeiou]y$/.test(label) ? `${label.slice(0, -1)}ies` : `${label}s`;

// A composition line, e.g. "1 local government area · 55 suburbs" — a count
// of the given places grouped by type, in the order the types first appear.
// Used both for the whole inventory (under the heading) and for a single
// group's leaves (on the group header).
const composition = (places: Place[]) => {
  const counts = new Map<string, number>();
  for (const place of places) {
    counts.set(place.placeType, (counts.get(place.placeType) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([placeType, n]) => {
      const label = renderPlaceType(placeType).toLowerCase();
      return `${n} ${n === 1 ? label : pluralise(label)}`;
    })
    .join(' · ');
};

// The Inventory Home page's geographical-scope panel: what the inventory
// covers, as the *named place hierarchy* (the Place model carries no geometry,
// so this is a list, not a drawn map — and there is no places page, so this
// panel is itself the place-level surface, with no click-through).
//
// Places render at the grain the data has: leaves dominate real inventories
// and every parent's children are one type in practice, so each non-leaf
// place is a group — a header naming the place, its leaf children flowing as
// wrapped `·`-separated text beneath — rather than one row per place. The
// header's right-hand side summarises what's beneath it (the leaf
// composition), falling back to the place's own type when nothing is (the
// single-place inventory). A place whose subtree continues past its leaves
// (e.g. a country above regions) is a section header, closed with a divider;
// its non-leaf children follow as their own groups in tree order.
// Store-less + presentational; the page owns the query.
const GeographicalScope = (props: GeographicalScopeProps) => {
  const { places } = props;
  const groups = groupPlaceTree(places);

  return (
    <Stack space='small'>
      <Stack space='xsmall'>
        <Heading as='h4' size='small'>
          Geographical scope
        </Heading>
        {places.length > 0 && <Secondary>{composition(places)}</Secondary>}
      </Stack>
      {places.length === 0 ? (
        <Placeholder>
          No places have been provisioned for this inventory.
        </Placeholder>
      ) : (
        <Content borderRadiusSize='small'>
          <Card>
            <Content
              padding='medium'
              paddingMode='equal'
              sizing='fill-container'
            >
              <Stack space='medium'>
                {groups.map(({ place, leaves, section }) => (
                  <Stack key={place.id} space='xsmall'>
                    <Inline space='medium'>
                      <InlineItem sizing='fill-container'>
                        <Strong>{place.name}</Strong>
                      </InlineItem>
                      <InlineItem>
                        <Secondary>
                          {leaves.length > 0
                            ? composition(leaves)
                            : renderPlaceType(place.placeType)}
                        </Secondary>
                      </InlineItem>
                    </Inline>
                    {leaves.length > 0 && (
                      <Text>{leaves.map((leaf) => leaf.name).join(' · ')}</Text>
                    )}
                    {section && <Divider />}
                  </Stack>
                ))}
              </Stack>
            </Content>
          </Card>
        </Content>
      )}
    </Stack>
  );
};

export type GeographicalScopeProps = {
  places: Place[];
};

export { GeographicalScope };
